import React, { FC, useMemo } from 'react';
import { faSignOut } from '@fortawesome/free-solid-svg-icons/faSignOut';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';

import Link from '@buyside/components/presentation/Link/link';
import NavigationUserInfo from '@buyside/components/presentation/MainSiteNavigation/TopNavigation/NavigationUser/NavigationUserInfo/navigationUserInfo';
import NavigationUserHeader from '@buyside/components/presentation/MainSiteNavigation/TopNavigation/NavigationUser/navigationUserHeader';

import useDropdownButton from '@buyside/util/hooks/useDropdownButton';
import { useIsMobile, BREAKPOINTS } from '@buyside/util/hooks/useIsMobile';
import {
  adminUrl,
  accessIdeasUrl,
  allocatorProfileUrl,
  billingInformationUrl,
  contributorDashboardFundProfileUrl,
  contributorDashboardInsightsUrl,
  emailSettingUrl,
  jobPostingUrl,
  logoutUrl,
  profileUrl,
  quickPositionsPageUrl,
  userAccountIdeasUrl,
  userAccountUrl,
  userJobApplicationsUrl,
  userReadingListUrl,
  userReferralsUrl,
  userWatchlistUrl
} from '@buyside/util/urlHelper';

import { MainNavigationCurrentUserQuery } from '@interfaces/graphql-typings';

import styles from './navigationUserStyles.scss';

const NavigationUser: FC<{ currentUser: MainNavigationCurrentUserQuery['currentUser'] }> = ({
  currentUser
}) => {
  const isMobile = useIsMobile(BREAKPOINTS.XMEDIUM);
  const { toggleDropdown, dropdownRef, dropdownVisible } = useDropdownButton();

  const items = useMemo(
    () => [
      {
        name: 'Your Account',
        href: userAccountUrl(),
        show: true
      },
      { name: 'Your Watchlist', href: userWatchlistUrl(), show: true },
      { name: 'Your Bookmarked Ideas', href: userReadingListUrl(), show: true },
      {
        name: 'Your Profile',
        href: currentUser?.profile ? profileUrl(currentUser?.profile.id) : '',
        show: Boolean(currentUser?.canEditProfile)
      },
      {
        name: 'Your Subscription',
        href: billingInformationUrl(),
        show: currentUser?.hasActiveSubscription
      },
      {
        name: 'Your Fund Profile',
        href: contributorDashboardFundProfileUrl(),
        show: Boolean(currentUser?.isFundProfileAdmin)
      },
      {
        name: 'Your Allocator Profile',
        href: allocatorProfileUrl(currentUser?.allocatorProfile?.id || ''),
        show: Boolean(currentUser?.allocatorProfile)
      },
      {
        name: 'Your Ideas',
        href: userAccountIdeasUrl(),
        show: Boolean(currentUser?.hasIdeas)
      },
      {
        name: 'Your Insights',
        href: contributorDashboardInsightsUrl(),
        show: Boolean(currentUser?.isBuyside)
      },
      {
        name: 'Your Job Postings',
        href: jobPostingUrl(),
        show: Boolean(currentUser?.hasJobPostings)
      },
      {
        name: 'Your Job Applications',
        href: userJobApplicationsUrl(),
        show: Boolean(currentUser?.hasJobApplications)
      },
      { name: 'Your Quick Positions', href: quickPositionsPageUrl(), show: true },
      { name: 'Your Referrals', href: userReferralsUrl(), show: true },
      {
        name: 'Upgrade',
        href: accessIdeasUrl(),
        show: Boolean(currentUser?.isRetail && currentUser.isTokenBased)
      },
      {
        name: 'Manage Email Notifications',
        href: emailSettingUrl(),
        show: true
      },
      { name: 'Contact Support', href: 'mailto:support@sumzero.com', show: true },
      { name: 'Admin', href: adminUrl(), show: Boolean(currentUser?.isAdmin) },
      { name: 'Sign Out', href: logoutUrl(), show: true, icon: faSignOut }
    ],
    [currentUser]
  );

  return (
    <div ref={dropdownRef} className={styles.dropdown}>
      <button
        className={classNames(styles.dropdownButton, styles.userButton, {
          [styles.active]: dropdownVisible
        })}
        onClick={toggleDropdown}
      >
        {currentUser?.profileImageUrl && <img src={currentUser.profileImageUrl} />}
        {isMobile ? null : currentUser?.displayName.split(' ')[0]}
      </button>
      <div
        className={classNames(styles.dropdownAreaWrapper, styles.userDropdownArea, {
          [styles.visible]: dropdownVisible
        })}
      >
        <div className={classNames(styles.dropdownArea)}>
          <NavigationUserHeader
            currentEmployers={currentUser?.currentEmployers || []}
            displayName={currentUser?.displayName || ''}
            isAllocator={Boolean(currentUser?.isAllocator)}
            profileId={currentUser?.profile?.id || ''}
            profileImageUrl={currentUser?.profileImageUrl || ''}
          />
          <NavigationUserInfo canWriteIdeas={Boolean(currentUser?.canWriteIdeas)} />
          <div className={styles.accountList}>
            {items.map(({ show, href, name, icon }) =>
              show ? (
                <Link to={href} key={name}>
                  {icon && <FontAwesomeIcon icon={icon} />}
                  {name}
                </Link>
              ) : null
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default NavigationUser;
